// src/demo/liveAdapters.js
//
// Translates the demo seed (src/demo/seed.js) into the SHAPES the
// existing live components expect. The seed was designed clean
// (stage / starts_at / duration_seconds / threads with nested
// messages); the live code grew up around Supabase rows that use
// different field names (status / appointmentDate / followUpDate /
// flat `messages` table). These adapters bridge the gap so the
// audit-found empty screens (Leads Today's Focus, Pipeline, SMS
// Inbox, Calendar) populate without rewriting the views.
//
// Each adapter is PURE — no React, no Supabase, no side effects.
// Safe to call on every render (CommissionTracker memoizes them).

import DEMO_SEED from './seed';

// Map the seed lead `stage` enum to the live `status` enum the
// kanban/today filters read.
const STAGE_TO_STATUS = {
  new:               'new',
  opt_in_pending:    'new',
  appointment_set:   'appt_set',
  soa_pending:       'appt_set',
  in_window:         'showed',
  quoted_followup:   'quoted',
  underwriting:      'pending_uw',
  client_active:     'closed_won',
  no_show:           'no_show',
  aep_review_due:    'showed',
};

// Seed leads should land in real-looking buckets when the demo
// loads "today":
//   - Today's appointment: John Smith (appointment_set lead 0)
//     and a second one for visual density
//   - Overdue follow-ups: stages that imply a missed callback
//   - Going cold: synthesize statusChangedAt 8+ days back
const OVERDUE_STAGES        = new Set(['quoted_followup', 'opt_in_pending', 'no_show']);
const TODAYS_APPT_STAGES    = new Set(['appointment_set']);

function todayDateStr() {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(days) {
  return new Date(Date.now() - days * 24 * 3600_000).toISOString();
}

/**
 * Adapt seed leads to the live lead shape consumed by LeadsView,
 * SmsInboxView, DashboardHome, CallWidget, and the kanban/pipeline
 * filters.
 *
 * Adds: status, appointmentDate, followUpDate, statusChangedAt,
 *       lastContactedAt. Preserves every other seed field so views
 *       that read by name (notes, score, tags, source) still work.
 */
export function buildLiveLeads(seed = DEMO_SEED) {
  const today = todayDateStr();
  return (seed.leads || []).map((l, i) => {
    const status = STAGE_TO_STATUS[l.stage] || 'new';
    const appointmentDate = TODAYS_APPT_STAGES.has(l.stage) ? today : null;
    const followUpDate    = OVERDUE_STAGES.has(l.stage) ? isoDaysAgo(1) : null;
    // Going-cold spread: ladder 0-12 days back so a handful of leads
    // satisfy the >=7-day filter without ALL of them looking stale.
    const statusChangedAt = isoDaysAgo(Math.min(12, i));
    const lastContactedAt = isoDaysAgo(Math.min(8, Math.floor(i / 2)));
    return {
      ...l,
      status,
      appointmentDate,
      followUpDate,
      statusChangedAt,
      lastContactedAt,
      // Activity stub so any "first activity = imported" check
      // doesn't trip and route this lead into the import-batch
      // rollback chip.
      activity: l.activity || [],
    };
  });
}

/**
 * Adapt seed calls to the shape live components read.
 *
 * Live code expects: id, leadId, phone (we derive from lead),
 * direction, disposition, durationSec, at, notes.
 */
export function buildLiveCalls(seed = DEMO_SEED) {
  const leadById = Object.fromEntries((seed.leads || []).map(l => [l.id, l]));
  return (seed.calls || []).map(c => {
    const lead = leadById[c.lead_id];
    return {
      id:          c.id,
      leadId:      c.lead_id,
      phone:       lead?.phone || null,
      direction:   c.direction,
      disposition: c.dispo,
      durationSec: c.duration_seconds || 0,
      at:          c.started_at,
      notes:       c.notes || null,
      source:      'demo',
    };
  });
}

/**
 * Adapt seed calendar entries to the shape live components read.
 *
 * Live code expects: id, title, when (ISO), durationMin, leadId, status.
 */
export function buildLiveCalendar(seed = DEMO_SEED) {
  return (seed.calendar || []).map(c => ({
    id:          c.id,
    title:       c.title,
    when:        c.starts_at,
    durationMin: c.duration_minutes || 30,
    leadId:      c.lead_id,
    status:      c.status,
    type:        c.type,
  }));
}

/**
 * Flatten seed inbox threads into the flat `messages` shape the
 * SmsInboxView consumes from Supabase. The view derives threads
 * itself from this flat list (groups by lead_id, sorts by sent_at).
 */
export function flattenSeedMessages(seed = DEMO_SEED) {
  const out = [];
  for (const t of (seed.inbox_threads || [])) {
    for (const m of (t.messages || [])) {
      out.push({
        id:              `${t.id}__${m.id}`,
        lead_id:         t.lead_id,
        direction:       m.from === 'agent' ? 'outbound' : 'inbound',
        body:            m.body,
        sent_at:         m.at,
        delivery_status: 'delivered',
        source:          m.via ? `demo:${m.via}` : 'demo',
        twilio_sid:      `DEMO_${t.id}_${m.id}`,
      });
    }
  }
  // SmsInboxView expects newest-first.
  out.sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
  return out;
}

/* ─────────────────────────────────────────────────────────────
   Life Command Center / Book of Business / Messaging Command Center
   — added so those three redesigned pages (which query real data
   directly instead of accepting leads/calls/calendar props) also
   populate in Demo Mode. Same bridge role as the adapters above:
   the seed (src/demo/seed.js) is shaped clean; these adapters
   translate it into the exact row shapes the live components read
   (clients.select('*'), the transactions[] prop, and the
   /api/inbox/priority-threads response body).
   ───────────────────────────────────────────────────────────── */

/**
 * Adapt seed life clients to the shape ClientsView / BookOfBusinessBoard
 * read from `supabase.from('clients').select('*')`.
 */
export function demoClients(seed = DEMO_SEED) {
  return (seed.life_clients || []).map((c) => ({
    id:                c.id,
    name:              c.name,
    phone:             c.phone,
    email:             c.email,
    address:           c.address || null,
    state_abbr:        c.state_abbr || c.state || null,
    dob:               c.dob || null,
    policy:            c.policy || null,
    carrier:           c.carrier || null,
    prod_num:          null,
    billing_mode:      c.billing_mode || 'monthly',
    billing_status:    c.billing_status || 'active',
    draft_amount:      c.draft_amount ?? c.monthly_premium ?? null,
    issue_date:        c.issue_date || null,
    first_draft_date:  c.first_draft_date || null,
    close_date:        c.issue_date || null,
    vertical:          c.vertical || 'life',
    consent:           [],
    agent_notes:       '',
    created_at:        c.issue_date ? `${c.issue_date}T00:00:00.000Z` : new Date().toISOString(),
    source:            'demo',
  }));
}

/**
 * Adapt the seed's life book of business (14 named clients' policies +
 * 34 filler policies, src/demo/seed.js §12b/12c) into the shape
 * LifeCommandCenter reads off its `transactions` prop: policy
 * (dedupe key), insured, company (-> getCarrier), planCode (->
 * getProduct via resolveProduct's catalog/legacy match), monthlyPremium,
 * netToAccount/commAmt, isChargeback, activityDate, faceAmount.
 *
 * Commission dollar amount isn't stored in the seed rows (which only
 * carry premium + carrier/product identity) — it's derived here as a
 * lump first-year advance (a realistic multiple of monthly premium,
 * varied 6-9x so the trailing-12-month sparkline isn't a flat line),
 * with chargeback rows clawing back ~3 months' worth. Both match how
 * MyPoliciesView / the producer aggregation already treat advances vs.
 * chargebacks elsewhere in this file.
 */
export function demoTransactions(seed = DEMO_SEED) {
  return (seed.life_book_transactions || []).map((t, i) => {
    const monthlyPremium = Number(t.monthlyPremium) || 0;
    const netToAccount = t.isChargeback
      ? -Math.round(monthlyPremium * 3)
      : Math.round(monthlyPremium * (6 + (i % 4)));
    return {
      policy:        t.policy,
      insured:       t.insured,
      prodNum:       '',
      company:       t.company,
      mga:           '',
      planCode:      t.planCode || '',
      monthlyPremium,
      commPremium:   monthlyPremium,
      netToAccount,
      commAmt:       netToAccount,
      isChargeback:  !!t.isChargeback,
      activityDate:  t.activityDate,
      faceAmount:    Number(t.faceAmount) || 0,
      source:        'demo',
    };
  });
}

// Real server categorization (api/inbox/priority-threads.js) needs a
// full message-history window this seed doesn't carry per-thread —
// this mirrors the same rules using the thread-level summary fields
// the seed does carry (last_direction / last_ai / unread / minutesAgo).
function categorizeDemoThread(t) {
  const hoursSinceInbound = t.last_direction === 'inbound' ? t.minutesAgo / 60 : null;
  if (t.unread && hoursSinceInbound != null && hoursSinceInbound < 24) {
    return { category: 'needs_response', chips: [{ kind: 'unread', label: '1 unread', tone: 'gold' }], hoursSinceInbound };
  }
  if (t.last_ai && hoursSinceInbound == null) {
    return { category: 'ai_handling', chips: [{ kind: 'ai_active', label: 'AI responding', tone: 'purple' }], hoursSinceInbound };
  }
  if (t.last_direction === 'outbound' && hoursSinceInbound == null) {
    return { category: 'waiting', chips: [{ kind: 'waiting_client', label: 'Waiting on client', tone: 'muted' }], hoursSinceInbound };
  }
  return { category: 'ai_handling', chips: [], hoursSinceInbound };
}

/**
 * Adapt the seed's life book conversations (src/demo/seed.js §12d) into
 * the SAME response body shape CockpitInbox reads from
 * GET /api/inbox/priority-threads (threads[] + metrics + channel_counts
 * + total_threads) — see api/inbox/priority-threads.js. Timestamps are
 * computed relative to Date.now() (not the seed's static "today"
 * anchor used elsewhere) because freshness is the whole point of an
 * inbox — mirrors buildLiveLeads()'s isoDaysAgo() use above.
 */
export function demoLifeThreads(seed = DEMO_SEED) {
  const clientById = Object.fromEntries((seed.life_clients || []).map(c => [c.id, c]));
  const threads = (seed.life_book_threads || []).map((t, i) => {
    const client = clientById[t.client_ref] || {};
    const { category, chips, hoursSinceInbound } = categorizeDemoThread(t);
    const lastMessageAt = new Date(Date.now() - (t.minutesAgo || 0) * 60_000).toISOString();
    return {
      thread_id:          `demo-thread-${t.client_ref}`,
      client_id:          t.client_ref,
      lead_id:             null,
      name:                client.name || 'Unknown',
      phone:               client.phone || null,
      email:               client.email || null,
      product_label:       'Life',
      role_label:          'Client',
      last_message:        t.last_message,
      last_message_at:     lastMessageAt,
      last_direction:      t.last_direction,
      last_ai:             !!t.last_ai,
      channel:             t.channel || 'sms',
      unread_count:        t.unread ? 1 : 0,
      msg_count:           3,
      last_call_at:        t.channel === 'voicemail' ? lastMessageAt : null,
      assigned:            i !== 3 && i !== 5,
      category,
      urgency_chips:       chips,
      hours_since_inbound: hoursSinceInbound,
    };
  });

  const counts = {};
  for (const t of threads) counts[t.category] = (counts[t.category] || 0) + 1;
  const channelCounts = { sms: 0, email: 0, voicemail: 0 };
  for (const t of threads) channelCounts[t.channel] = (channelCounts[t.channel] || 0) + 1;

  return {
    ok: true,
    generated_at: new Date().toISOString(),
    section_filter: 'all',
    total_threads: threads.length,
    counts,
    channel_counts: channelCounts,
    metrics: {
      unread:              threads.reduce((s, t) => s + t.unread_count, 0),
      needs_me:            counts.needs_response || 0,
      ai_handling:         counts.ai_handling || 0,
      hot:                 0,
      overdue:             threads.filter(t => t.hours_since_inbound != null && t.hours_since_inbound > 24).length,
      conversations:       threads.length,
      unassigned:          threads.filter(t => !t.assigned).length,
      awaiting_reply:      threads.filter(t => t.last_direction === 'inbound').length,
      ai_active_count:     threads.filter(t => t.last_ai).length,
      response_rate_pct:   87,
      avg_reply_minutes:   38,
    },
    threads,
  };
}
